

import React, { useEffect, useState, useRef, useMemo } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Typography from "@mui/material/Typography";
import SocialCard from "examples/Cards/SocialCard";
import EntityCard from "examples/Cards/EntityCard";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service";
import { Divider } from "@mui/material";
import MDInput from "components/MDInput";
import UpgradePopup from "layouts/components/Upgrade"; // ✅ IMPORTED
import SnapshotService from "services/snapshot-service";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import BrandingPackDialog from "../../../profile/components/Branding/BrandingPackDialog";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo";
import GuidanceBubble from "layouts/guidance/components/GuidanceBubble"; // [NEW]
import { usePermissions } from "context";

// Custom Tooltip
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
    textAlign: "left",
  },
});

const tooltipContent = (
  <div style={{ maxWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>Review Your Snapshot:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Confirm selected primary profile and any secondary items.<br />
      - Click “Generate Snapshot” to create your comparison report.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <strong>Tip:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - You can go back and make changes anytime before generating.
    </Typography>
  </div>
);

function ReviewAndGenerateSnapshot({
  snapshotType,
  primaryProfile,
  secondaryProfiles = [],
  campaignEntities = [],
  snapshotConfigRef,
  snapshotId = null, // 🆕 determines edit mode
  entitlements, // ✅ NEW PROP
  isLiveDemo = false, // [NEW] Live Demo flag
  forceEmailError = false, // [NEW] Prop to trigger inline error
  onSave, // [NEW] Prop for saving
  onCancel, // [NEW] Prop for canceling
  isSaving, // [NEW] Prop for saving state
  demoHighlight = false, // [NEW] Prop for highlighting button in demo mode
}) {
  const [snapshotName, setSnapshotName] = useState("");
  const [email, setEmail] = useState("");
  const [scheduleMonthly, setScheduleMonthly] = useState(false); // ✅ DEFAULT FALSE
  const [sendSnapshotImmediately, setSendSnapshotImmediately] = useState(false); // [NEW]
  const [emailError, setEmailError] = useState(false);
  const [internalError, setInternalError] = useState(false); // [NEW] Internal required error
  const [showUpgrade, setShowUpgrade] = useState(false); // ✅ NEW STATE for popup

  // 🎨 BRANDING STATE
  const [brandingPacks, setBrandingPacks] = useState([]);
  const [selectedBrandingPack, setSelectedBrandingPack] = useState(null);
  const [targetClient, setTargetClient] = useState("");
  const [brandingMenu, setBrandingMenu] = useState(null);

  // 👁️ PREVIEW STATE
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // BRANDING DIALOG & RBAC STATE
  const [openBrandingDialog, setOpenBrandingDialog] = useState(false);
  const [showLiveDemoPopup, setShowLiveDemoPopup] = useState(false);
  const [upgradeType, setUpgradeType] = useState("feature");
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("Snapshot");
  const [upgradePlanRequired, setUpgradePlanRequired] = useState("Higher Plan");
  const [showRBACConfig, setShowRBACConfig] = useState(false);
  const [rbacConfigRole, setRbacConfigRole] = useState("Manager");
  const { hasPermission } = usePermissions();

  const requiredRoles = {
    "branding:manage": "Manager",
  };

  // Sync internal error with prop
  useEffect(() => {
    if (forceEmailError) setInternalError(true);
  }, [forceEmailError]);

  const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

  const resolvedPrimary = primaryProfile || secondaryProfiles[0];
  const remainingSecondary = Array.isArray(secondaryProfiles)
    ? secondaryProfiles.filter((p) => p !== resolvedPrimary)
    : [];

  // ✅ ENTITLEMENT CHECK
  const FEATURE_KEY = "Snapshot Scheduling";
  const isLocked = entitlements?.features && !entitlements.features.includes(FEATURE_KEY);

  // ✅ Force uncheck and clear email if locked
  useEffect(() => {
    if (isLocked) {
      if (scheduleMonthly) setScheduleMonthly(false);
      if (email) setEmail("");
    }
  }, [isLocked, scheduleMonthly, email]);

  // 📏 Height logic
  const handleSaveClick = () => {
    if (onSave) onSave();
  };
  const imageHeightsRef = useRef([]);
  const [heightDone, setHeightDone] = useState(false);

  const registerHeight = (h) => {
    imageHeightsRef.current.push(Math.round(h));
    if (imageHeightsRef.current.length === campaignEntities.length) {
      setHeightDone(true);
    }
  };

  const forcedHeight = useMemo(() => {
    if (!heightDone) return null;
    const counts = {};
    imageHeightsRef.current.forEach((h) => {
      counts[h] = (counts[h] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const MAX_HEIGHT = 300;
    return sorted.length > 0 ? Math.min(Number(sorted[0][0]), MAX_HEIGHT) : null;
  }, [heightDone]);

  // 🎨 FETCH BRANDING PACKS
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await WorkspaceService.fetchWorkspaceBrandingPacks();
        let packs = [];
        if (Array.isArray(res)) {
          packs = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          packs = res.data;
        }
        setBrandingPacks(packs);

        // Auto-select default branding pack for NEW snapshots (only once on load)
        if (!snapshotId && packs.length > 0) {
          const defaultPack = packs.find(p => p.isDefault);
          if (defaultPack) {
            setSelectedBrandingPack(defaultPack);
          }
        }
      } catch (err) {
        console.warn("⚠️ Error fetching branding packs:", err);
      }
    };
    fetchBranding();
  }, [snapshotId]);

  const handleBrandingCreated = (newPack) => {
    if (newPack) {
      setBrandingPacks((prev) => [...prev, newPack]);
      setSelectedBrandingPack(newPack);
    }
    setOpenBrandingDialog(false);
  };

  // 🧠 Initialize name/email for create or edit mode
  useEffect(() => {
    const initializeSnapshotFields = async () => {
      try {
        if (snapshotId) {
          // 🟡 EDIT MODE: Fetch from backend
          const snapshot = await WorkspaceService.fetchWorkspaceSnapshots(snapshotId);
          console.log("Fetched snapshot from backend:", snapshot);

          if (snapshot) {
            setSnapshotName(snapshot.snapshot_name || "");
            if (Array.isArray(snapshot.recipient_emails)) {
              setEmail(snapshot.recipient_emails.join(", "));
            } else if (snapshot.recipient_emails) {
              setEmail(snapshot.recipient_emails.toString());
            }
            // Respect saved setting, unless locked (handled by other useEffect)
            setScheduleMonthly(snapshot.scheduleEnabled ?? false);
          }
        } else {
          // 🆕 NEW SNAPSHOT MODE: Auto-populate defaults
          const defaultName = `${snapshotType} Snapshot - ${resolvedPrimary?.name || "Untitled"
            }`;
          setSnapshotName(defaultName);

          // ✅ ONLY POPULATE EMAIL IF ENTITLED
          if (!isLocked) {
            try {
              const res = await AuthService.getProfile();
              const userEmail = res?.data?.attributes?.email || "";
              setEmail(userEmail);
            } catch (err) {
              console.warn("⚠️ Could not auto-populate user email:", err);
            }
          }

          setScheduleMonthly(false); // ✅ DEFAULT TO FALSE
        }

        // 🎨 Handle Branding Pack Initialization (if editing)
        if (snapshotId) {
          const snapshot = await WorkspaceService.fetchWorkspaceSnapshots(snapshotId);
          if (snapshot?.branding_pack_id) {
            // We'll handle selecting the actual object in the brandingPacks dependency useEffect below
          }
          if (snapshot?.target_client) {
            setTargetClient(snapshot.target_client);
          }
        }
      } catch (err) {
        console.error("❌ Error initializing snapshot fields:", err);
      }
    };

    // ✅ Always run initialization when entering this screen
    initializeSnapshotFields();
    // Include resolvedPrimary.name to update default name dynamically
  }, [snapshotId, snapshotType, resolvedPrimary?.name, isLocked]);


  // 🔄 Sync configRef with latest state
  useEffect(() => {
    if (snapshotConfigRef) {
      snapshotConfigRef.current = {
        snapshotId,
        snapshotName,
        email,
        scheduleMonthly,
        sendSnapshotImmediately, // [NEW]
        branding_pack_id: selectedBrandingPack?._id || null, // [NEW]
        target_client: targetClient, // [NEW]
      };
    }
  }, [snapshotName, email, scheduleMonthly, sendSnapshotImmediately, selectedBrandingPack, targetClient, snapshotId, snapshotConfigRef]);

  // 🎨 Sync selected branding pack when packs load or snapshotId changes
  useEffect(() => {
    if (snapshotId && brandingPacks.length > 0) {
      const init = async () => {
        const snapshot = await WorkspaceService.fetchWorkspaceSnapshots(snapshotId);
        if (snapshot?.branding_pack_id) {
          const pack = brandingPacks.find(p => p._id === snapshot.branding_pack_id);
          if (pack) setSelectedBrandingPack(pack);
        }
      };
      init();
    }
  }, [snapshotId, brandingPacks]);

  const handleEmailChange = (e) => {
    const input = e.target.value;
    setEmail(input);
    setInternalError(false); // ✅ Clear required error on change
    const emailList = input
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    setEmailError(emailList.some((email) => !emailRegex.test(email)));
  };

  // ✅ HANDLE CHECKBOX CHANGE
  const handleCheckboxChange = (e) => {
    if (isLocked && e.target.checked) {
      setShowUpgrade(true);
      return;
    }

    setScheduleMonthly(e.target.checked);
  };

  // ✅ HANDLE LOCKED EMAIL INTERACTION
  const handleEmailClick = () => {
    if (isLocked) {
      setShowUpgrade(true);
    }
  };

  // 👀 PREVIEW HANDLER
  const handlePreview = async () => {
    if (isPreviewing) return;
    setIsPreviewing(true);
    setPreviewError(null);

    try {
      const snapshotInfo = {
        snapshotType,
        snapshotName,
        primaryProfile: {
          platform: resolvedPrimary.platform,
          profileId: resolvedPrimary.profileId || resolvedPrimary.id
        },
        secondaryProfiles: remainingSecondary.map(p => ({
          platform: p.platform,
          profileId: p.profileId || p.id
        })),
        branding_pack_id: selectedBrandingPack?._id || "system_default",
        target_client: targetClient,
        rangeStart: dayjs.utc().subtract(1, "month").startOf("month").toISOString(),
        rangeEnd: dayjs.utc().subtract(1, "month").endOf("month").toISOString()
      };

      const fileUrl = await SnapshotService.previewSnapshot(snapshotInfo);
      setPreviewUrl(fileUrl);
      setPreviewOpen(true);
    } catch (err) {
      console.error("❌ Error previewing snapshot:", err);
      setPreviewError("Failed to generate preview. Please try again.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const reportingPeriod = `${dayjs()
    .subtract(1, "month")
    .startOf("month")
    .format("D MMM YYYY")} to ${dayjs()
      .subtract(1, "month")
      .endOf("month")
      .format("D MMM YYYY")}`;

  return (
    <MDBox mt={-3}>
      <Card>
        <MDBox>
          {/* Header */}
          <MDBox
            mx={2}
            mt={-3}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              {snapshotId ? "Review & Save Snapshot" : "Review & Save Snapshot"}
            </MDTypography>
            <CustomTooltip title={tooltipContent} placement="bottom" arrow>
              <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          </MDBox>

          <MDBox p={3}>
            <Grid container spacing={3}>
              {/* Snapshot Name */}
              <Grid item xs={12} md={6}>
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text"
                    sx={{ marginBottom: 1, display: "block", textAlign: "left" }}
                  >
                    Snapshot Name
                  </MDTypography>
                  <MDBox
                    sx={{
                      borderRadius: "16px",
                      backgroundColor: "#f0f2f5",
                      padding: "16px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MDInput
                      fullWidth
                      variant="outlined"
                      placeholder="Enter Snapshot Name"
                      value={snapshotName}
                      onChange={(e) => setSnapshotName(e.target.value)}
                      disabled={isLiveDemo}
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: "12px",
                          backgroundColor: "white !important",
                          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                          height: "44px"
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Target Client */}
              <Grid item xs={12} md={6}>
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text"
                    sx={{ marginBottom: 1, display: "block", textAlign: "left" }}
                  >
                    Target Client / Company Name
                  </MDTypography>
                  <MDBox
                    sx={{
                      borderRadius: "16px",
                      backgroundColor: "#f0f2f5",
                      padding: "16px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MDInput
                      fullWidth
                      variant="outlined"
                      placeholder="e.g. Acme Corp (Optional)"
                      value={targetClient}
                      onChange={(e) => setTargetClient(e.target.value)}
                      disabled={isLiveDemo}
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: "12px",
                          backgroundColor: "white !important",
                          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                          height: "44px"
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>
              </Grid>


              {/* Branding Pack */}
              <Grid item xs={12}>
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text"
                    sx={{ marginBottom: 1, display: "block", textAlign: "left" }}
                  >
                    Branding Pack
                  </MDTypography>
                  <MDBox
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      flexWrap: "wrap",
                      gap: 2,
                      borderRadius: "16px",
                      backgroundColor: "#f0f2f5",
                      padding: "16px",
                    }}
                  >
                    <MDBox sx={{ flex: { xs: "none", sm: 4 }, width: { xs: "100%", sm: "auto" } }}>
                      <MDInput
                        select
                        fullWidth
                        variant="outlined"
                        value={selectedBrandingPack?._id || "default"}
                        onChange={(e) => {
                          const packId = e.target.value;
                          if (packId === "default") {
                            setSelectedBrandingPack(null);
                          } else {
                            const pack = brandingPacks.find(p => p._id === packId);
                            if (pack) setSelectedBrandingPack(pack);
                          }
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { sx: { maxHeight: 300 } }
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            borderRadius: "12px",
                            backgroundColor: "white !important",
                            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                            height: "44px"
                          },
                          "& .MuiSelect-select": {
                            paddingTop: "12px",
                            paddingBottom: "12px",
                            paddingLeft: "12px",
                            textAlign: "left"
                          }
                        }}
                      >
                        <MenuItem value="default">
                          <em>Comparedly Default</em>
                        </MenuItem>
                        {brandingPacks.map((pack) => (
                          <MenuItem key={pack._id} value={pack._id}>
                            {pack.name}
                          </MenuItem>
                        ))}
                      </MDInput>
                    </MDBox>
                    <Card
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                        gap: 1.5,
                        padding: "0 24px",
                        borderRadius: "12px",
                        backgroundColor: "white !important",
                        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                        height: "44px",
                        flex: { xs: "none", sm: 1 },
                        width: { xs: "100%", sm: "auto" },
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                      onClick={() => {
                        if (!hasPermission("branding:manage")) {
                          setRbacConfigRole(requiredRoles["branding:manage"] || "Manager");
                          setShowRBACConfig(true);
                          return;
                        }
                        setOpenBrandingDialog(true);
                      }}
                    >
                      <IconButton sx={{ p: 0, color: "#e91e63" }}>
                        <AddCircleIcon fontSize="small" />
                      </IconButton>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color="text"
                        sx={{ ml: 0.5, whiteSpace: "nowrap", fontSize: "0.875rem" }}
                      >
                        Add New Branding Pack
                      </MDTypography>
                    </Card>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Recipient Emails */}
              <Grid item xs={12}>
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text"
                    sx={{ marginBottom: 1, display: "block", textAlign: "left" }}
                  >
                    Recipient Emails (comma separated)
                  </MDTypography>
                  <MDBox
                    sx={{
                      borderRadius: "16px",
                      backgroundColor: "#f0f2f5",
                      padding: "16px",
                    }}
                  >
                    <MDInput
                      fullWidth
                      variant="outlined"
                      placeholder="e.g. info@comparedly.com"
                      value={email}
                      onChange={handleEmailChange}
                      error={emailError || internalError}
                      helperText={
                        emailError
                          ? "Please enter valid email addresses separated by commas."
                          : internalError
                            ? "At least one recipient email is required"
                            : ""
                      }
                      multiline
                      rows={2}
                      disabled={isLiveDemo}
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: "12px",
                          backgroundColor: "white !important",
                          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                          padding: "12px",
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>

                {/* Checkboxes */}
                <MDBox display="flex" flexDirection="column" mt={1} ml={0.5} gap={1}>
                  <MDBox display="flex" alignItems="center">
                    <Checkbox
                      checked={sendSnapshotImmediately}
                      onChange={(e) => setSendSnapshotImmediately(e.target.checked)}
                      disabled={isLiveDemo}
                      sx={{
                        p: 0,
                        mr: 1,
                        color: "#6c757d",
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                          border: sendSnapshotImmediately ? "none" : "2px solid #adb5bd",
                          borderRadius: "4px",
                          fill: sendSnapshotImmediately ? "currentColor" : "transparent"
                        },
                        "&.Mui-checked": { color: "info.main" }
                      }}
                    />
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Send a new snapshot immediately now
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center">
                    <Checkbox
                      checked={scheduleMonthly}
                      onChange={handleCheckboxChange}
                      disabled={isLiveDemo}
                      sx={{
                        p: 0,
                        mr: 1,
                        color: "#6c757d",
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                          border: scheduleMonthly ? "none" : "2px solid #adb5bd",
                          borderRadius: "4px",
                          fill: scheduleMonthly ? "currentColor" : "transparent"
                        },
                        "&.Mui-checked": { color: "info.main" }
                      }}
                    />
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Schedule this snapshot to be automatically delivered
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Details & Action Row */}
              <Grid item xs={12}>
                <MDBox
                  bgcolor="grey-100"
                  borderRadius="lg"
                  p={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  {/* Left: Details */}
                  <MDBox display="flex" alignItems="flex-start" gap={2}>
                    <Icon color="info" fontSize="small" sx={{ mt: 0.5 }}>info</Icon>
                    <MDBox sx={{ textAlign: "left" }}>
                      <MDTypography variant="button" fontWeight="bold" color="text" display="block">
                        Snapshot Details
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {scheduleMonthly ? (
                          <>Scheduled for automatic monthly delivery.</>
                        ) : (
                          <>This snapshot will be saved but not scheduled for automatic delivery.</>
                        )}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Content:</strong> Each snapshot will cover data from <strong>{reportingPeriod}</strong>.
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Right: Actions */}
                  <MDBox display="flex" justifyContent="flex-end" gap={2}>
                    {!isLiveDemo && (
                      <MDButton onClick={handlePreview} variant="outlined" color="dark" disabled={isSaving || isPreviewing}>
                        {isPreviewing ? <CircularProgress size={20} color="accent" /> : "Preview Snapshot"}
                      </MDButton>
                    )}
                    <MDButton onClick={onCancel} color="secondary" disabled={isSaving || isPreviewing}>
                      Cancel
                    </MDButton>
                    <MDBox position="relative">
                      {demoHighlight && (
                        <GuidanceBubble
                          visible={true}
                          message="Click Save Snapshot"
                        />
                      )}
                      <MDButton
                        onClick={handleSaveClick}
                        variant="contained"
                        color="accent"
                        disabled={isSaving || isPreviewing}
                        sx={{
                          transition: "transform 0.3s ease",
                          transform: demoHighlight ? "scale(1.1)" : "none",
                          zIndex: demoHighlight ? 1400 : "auto",
                          boxShadow: demoHighlight ? "0 0 15px rgba(255, 179, 71, 0.6)" : undefined
                        }}
                      >
                        {isSaving ? (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} color="white" />
                            Saving...
                          </MDBox>
                        ) : (
                          "Save Snapshot"
                        )}
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>

          </MDBox>
        </MDBox>
      </Card >

      <BrandingPackDialog
        open={openBrandingDialog}
        onClose={() => setOpenBrandingDialog(false)}
        onSuccess={handleBrandingCreated}
        isLiveDemo={isLiveDemo}
      />

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "90vh", maxWidth: "1200px" }
        }}
      >
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          Snapshot Preview
          <IconButton onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              width="100%"
              height="100%"
              title="Snapshot Preview"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      <LiveDemoRestriction
        open={showLiveDemoPopup}
        onClose={() => setShowLiveDemoPopup(false)}
        featureName="Saving Snapshot"
      />

      <UpgradePopup
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type={upgradeType}
        featureName={upgradeFeatureName}
        planRequired={upgradePlanRequired}
        currentPlan={entitlements?.plan}
      />

      <UpgradePopup
        open={showRBACConfig}
        onClose={() => setShowRBACConfig(false)}
        type="rbac"
        featureName="Managing Branding Packs"
        roleRequired={rbacConfigRole}
      />
    </MDBox >
  );
}

export default ReviewAndGenerateSnapshot;